import React, { useEffect, useRef, useState } from "react";
import { Check, CalendarDays, Pencil, Trash2, Undo2 } from "lucide-react";
import { fmtTime, fmtDateTime } from "../lib/format";
import { omezenyPohyb } from "../lib/mobil";
import api, { apiError } from "../lib/api";
import { ikonaTypu } from "../lib/typyAktivit";

/**
 * Řádek aktivity, který jde na telefonu tahat prstem. Převzato z Proyekt
 * (`SwipeRow`), přizpůsobeno našim aktivitám a designu.
 *
 *   tah doprava ≥ 90 px  → hotovo (u hotové aktivity „vrátit")
 *   tah doleva           → řádek se zastaví na pásu akcí a zůstane odemčený
 *   přidržení 550 ms     → smazat (s potvrzením)
 *
 * Doleva se řádek NIKDY nedotáhne přes pás akcí — dotažené mazání jsme
 * z Proyekt nepřevzali, kolidovalo s odemykáním tlačítek (jejich zadání 101).
 * Odemčený je vždy jen jeden řádek: `openId` drží stránka.
 */

const SIRKA_AKCE = 56;      // šířka jednoho tlačítka v pásu — 44 px na prst + okraj
const PRAH_HOTOVO = 90;
const PRAH_ODEMCENI = 28;
const PRIDRZENI_MS = 550;

const STITEK = {
  po_terminu: { text: "Po termínu", cls: "text-danger bg-[#fdeeec]" },
  hotova: { text: "Hotovo", cls: "text-success bg-success-bg" },
  // Modrá, ne jantarová — v kalendáři platí „barva = stav" a otevřené je modré.
  otevrena: { text: "Otevřené", cls: "text-[#2c6ecb] bg-[#eef4fd]" },
};

const jeDnes = (iso) => {
  if (!iso) return false;
  const d = new Date(iso), t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
};

export function SwipeAktivita({
  a, onOpen, onHotovo, onDnes, onUpravit, onSmazat, smiUpravit,
  openId, setOpenId, cas = "datum", podradek,
}) {
  const [dx, setDx] = useState(0);
  const [tahne, setTahne] = useState(false);
  const startX = useRef(0);
  const pridrzeni = useRef(null);
  const zachyceno = useRef(false);
  const praveTahl = useRef(false);
  const zKlidu = useRef(true);
  const vychozi = useRef(0);

  const hotova = a.stav === "HOTOVA";
  const TypIkona = ikonaTypu(a.typ);   // hovor / schůzka / e-mail… na první pohled
  const otevreny = openId === a.id;
  const stitek = a.po_terminu ? STITEK.po_terminu : hotova ? STITEK.hotova : STITEK.otevrena;
  const puntik = a.po_terminu ? "bg-danger" : hotova ? "bg-success" : "bg-[#2c6ecb]";

  // Pás akcí: „Dnes" jen u otevřené aktivity, která dnes není — jinak by
  // tlačítko nic nedělalo. Bez práva upravovat je pás prázdný a řádek se
  // doleva vůbec nehne.
  const akce = smiUpravit ? [
    ...(!hotova && !jeDnes(a.date_start) && onDnes
      ? [{ klic: "dnes", text: "Dnes", Ikona: CalendarDays, cls: "bg-inputbg text-sec", fn: () => onDnes(a) }] : []),
    ...(onUpravit ? [{ klic: "upravit", text: "Upravit", Ikona: Pencil, cls: "bg-[#fff2e8] text-accent-hover", fn: () => onUpravit(a) }] : []),
    ...(onSmazat ? [{ klic: "smazat", text: "Smazat", Ikona: Trash2, cls: "bg-danger text-white", fn: () => onSmazat(a) }] : []),
  ] : [];
  const sirkaPasu = akce.length * SIRKA_AKCE;

  useEffect(() => { if (!otevreny && !tahne) setDx(0); }, [otevreny, tahne]);

  const zrusPridrzeni = () => { if (pridrzeni.current) { clearTimeout(pridrzeni.current); pridrzeni.current = null; } };
  // Po tahu se jedno kliknutí spolkne, jinak by se hned otevřel detail.
  const oznacTah = () => { praveTahl.current = true; setTimeout(() => { praveTahl.current = false; }, 400); };

  const naDolu = (e) => {
    zKlidu.current = !otevreny;
    vychozi.current = otevreny ? -sirkaPasu : 0;
    startX.current = e.clientX; zachyceno.current = false; setTahne(true);
    zrusPridrzeni();
    // Přidržení maže jen řádek v klidu, ne odemčený.
    if (smiUpravit && onSmazat && zKlidu.current) {
      pridrzeni.current = setTimeout(() => {
        pridrzeni.current = null; setTahne(false); setDx(0); setOpenId(null); oznacTah(); onSmazat(a);
      }, PRIDRZENI_MS);
    }
  };

  const naPohyb = (e) => {
    if (!tahne) return;
    let d = vychozi.current + (e.clientX - startX.current);
    if (Math.abs(e.clientX - startX.current) > 6) {
      zrusPridrzeni();
      if (!zachyceno.current) {
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* starší prohlížeč */ }
        zachyceno.current = true;
      }
    }
    if (!smiUpravit && d > 0) d = 0;
    d = Math.max(-sirkaPasu, Math.min(160, d));
    setDx(d);
  };

  const naNahoru = () => {
    zrusPridrzeni();
    if (!tahne) return;
    setTahne(false);
    const d = dx;
    if (Math.abs(d - vychozi.current) > 6) oznacTah();
    // Zavření odemčeného řádku nesmí nic spustit.
    if (!zKlidu.current) { oznacTah(); setDx(0); setOpenId(null); return; }
    if (d >= PRAH_HOTOVO && smiUpravit) { setDx(0); setOpenId(null); onHotovo(a); return; }
    if (sirkaPasu && d <= -Math.max(PRAH_ODEMCENI, sirkaPasu * 0.35)) { setDx(-sirkaPasu); setOpenId(a.id); return; }
    setDx(0); if (otevreny) setOpenId(null);
  };

  const naKlik = () => {
    if (praveTahl.current) return;
    if (otevreny) { setOpenId(null); setDx(0); return; }
    onOpen(a);
  };

  const bezAnimace = tahne || omezenyPohyb();

  return (
    <div className="relative overflow-hidden rounded-card bg-inputbg" data-testid={`swipe-${a.id}`}>
      {/* pruh při tahu doprava: zelené Hotovo, u hotové tmavé Vrátit */}
      <div className={`absolute inset-y-0 left-0 flex items-center gap-1.5 pl-4 text-white t-telo font-700 ${
        hotova ? "bg-ink" : "bg-success"}`}
        style={{ width: zKlidu.current ? Math.max(0, dx) : 0 }}>
        {zKlidu.current && dx > 40 && (hotova
          ? <><Undo2 size={18} /> Vrátit</>
          : <><Check size={18} strokeWidth={2.6} /> Hotovo</>)}
      </div>

      {/* pás akcí vpravo — při tahu doprava se schová */}
      <div className="absolute inset-y-0 right-0 flex" data-testid={`swipe-akce-${a.id}`}
        style={{ opacity: dx > 0 ? 0 : 1, pointerEvents: dx > 0 ? "none" : undefined }}>
        {akce.map((x) => (
          <button key={x.klic} type="button" data-testid={`swipe-${x.klic}-${a.id}`}
            onClick={(e) => { e.stopPropagation(); oznacTah(); setOpenId(null); setDx(0); x.fn(); }}
            className={`flex flex-col items-center justify-center gap-0.5 ${x.cls}`} style={{ width: SIRKA_AKCE }}>
            <x.Ikona size={17} /><span className="text-[10px] font-700">{x.text}</span>
          </button>
        ))}
      </div>

      {/* vlastní řádek */}
      <div onPointerDown={naDolu} onPointerMove={naPohyb} onPointerUp={naNahoru} onPointerCancel={naNahoru}
        onClick={naKlik} role="button" tabIndex={0} data-testid={`activity-${a.id}`}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(a); } }}
        style={{ transform: `translateX(${dx}px)` }}
        className={`relative flex items-start gap-3 px-4 py-3 min-h-[60px] bg-white border border-line-softer rounded-card
          touch-pan-y select-none ${bezAnimace ? "" : "transition-transform duration-200"} motion-reduce:transition-none`}>
        <span className={`h-2 w-2 rounded-full shrink-0 mt-[7px] ${puntik}`} />
        {/* Název má celou šířku — na 390 px se vedle času a štítku sekal na
            „Zaměření r…". Čas a štítek jdou na druhý řádek. */}
        <span className="min-w-0 flex-1">
          <span className={`block t-telo font-600 truncate ${hotova ? "text-muted line-through" : "text-ink"}`}>
            {a.nazev}
          </span>
          <span className="mt-1 flex items-center gap-2">
            <span className="amount t-meta font-600 text-sec-3 shrink-0 inline-flex items-center gap-1" title={a.typ || ""}>
              <TypIkona size={13} strokeWidth={2.2} />
              {cas === "cas" ? fmtTime(a.date_start) : fmtDateTime(a.date_start)}
            </span>
            <span className="t-meta text-muted truncate flex-1">
              {podradek ? podradek(a) : [a.typ, a.zarizeni_nazev, (a.osoby_jmena || a.osoby_names || [])[0]].filter(Boolean).join(" · ")}
            </span>
            <span className={`label-up px-2 py-0.5 rounded-pill shrink-0 ${stitek.cls}`}>{stitek.text}</span>
          </span>
        </span>
      </div>
    </div>
  );
}

/** Jednořádková nápověda nad seznamem — jen na telefonu, jen když jde tahat. */
export function SwipeNapoveda({ smiUpravit }) {
  if (!smiUpravit) return null;
  return (
    <p className="t-meta text-muted-2 px-1" data-testid="swipe-napoveda">
      Táhni doprava → hotovo · doleva → akce · přidrž → smazat
    </p>
  );
}

/** Spodní šuplík — potvrzení, které jde odklepnout palcem. */
function Suplik({ open, onClose, titulek, testid, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center md:p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[420px] rounded-t-sheet md:rounded-card shadow-panel
        px-[18px] pt-3 pb-[calc(24px_+_env(safe-area-inset-bottom))] md:p-6 space-y-3">
        <span className="md:hidden mx-auto mb-1 block h-1 w-[38px] rounded-pill bg-line-strong" />
        <div className="t-sekce text-ink">{titulek}</div>
        {children}
      </div>
    </div>
  );
}

/**
 * Obsluha tahů na jednom místě, ať Domů, karta klienta i detail případu
 * dělají totéž. Stránka dodá `reload` a `onUpravit` (otevře svůj formulář).
 */
export function useAktivitaSwipe({ reload, onUpravit }) {
  const [openId, setOpenId] = useState(null);
  const [keSmazani, setKeSmazani] = useState(null);
  const [chyba, setChyba] = useState("");

  const hotovo = async (a) => {
    try { await api.post(`/api/aktivity/${a.id}/hotovo`); reload?.(); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
  };

  // „Dnes" = přesunout na dnešek se stejným časem a stejnou délkou.
  const dnes = async (a) => {
    const start = new Date(a.date_start);
    const delka = a.date_end ? new Date(a.date_end) - start : 0;
    const novy = new Date();
    novy.setHours(start.getHours(), start.getMinutes(), 0, 0);
    try {
      await api.patch(`/api/aktivity/${a.id}`, {
        date_start: novy.toISOString(),
        date_end: delka ? new Date(novy.getTime() + delka).toISOString() : null,
      });
      reload?.();
    } catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
  };

  const smazat = async () => {
    const a = keSmazani;
    setKeSmazani(null);
    try { await api.delete(`/api/aktivity/${a.id}`); reload?.(); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
  };

  const swipeProps = {
    openId, setOpenId, onHotovo: hotovo, onDnes: dnes,
    onUpravit: onUpravit ? (a) => { setOpenId(null); onUpravit(a); } : null,
    onSmazat: (a) => { setOpenId(null); setKeSmazani(a); },
  };

  const supliky = (
    <>
      <Suplik open={!!keSmazani} onClose={() => setKeSmazani(null)} titulek="Smazat aktivitu?" testid="smazat-aktivitu-suplik">
        <p className="t-meta text-muted">„{keSmazani?.nazev}" zmizí z kalendáře i z klienta. Vrátit to nejde.</p>
        <button onClick={smazat} data-testid="smazat-aktivitu-potvrdit"
          className="tap w-full h-12 rounded-[16px] bg-danger text-white t-telo font-700">Smazat</button>
        <button onClick={() => setKeSmazani(null)} data-testid="smazat-aktivitu-zrusit"
          className="tap w-full h-11 pill border border-line-strong t-telo font-600 text-sec">Nechat</button>
      </Suplik>
      {chyba && (
        <div className="fixed left-4 right-4 bottom-[calc(96px_+_env(safe-area-inset-bottom))] z-[60] card px-4 py-3 flex items-center gap-3"
          data-testid="swipe-chyba">
          <span className="h-2 w-2 rounded-full bg-danger shrink-0" />
          <span className="t-meta text-ink flex-1">{chyba}</span>
          <button onClick={() => setChyba("")} className="t-meta font-600 text-muted">Zavřít</button>
        </div>
      )}
    </>
  );

  return { swipeProps, supliky, openId, setOpenId };
}
